import { ArrowLeft, Upload, X, Star, Image as ImageIcon, GripVertical } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { usePostHog } from 'posthog-js/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { authenticatedFetch } from '@/lib/api';
import type { UnitImage } from '@/types';

interface GalleryPageProps {
  // Optional: can be used for testing or as a standalone component
  unitId?: string;
  onBack?: () => void;
}

export function GalleryPage({ unitId: propUnitId, onBack: propOnBack }: GalleryPageProps) {
  const { unitId: paramUnitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const posthog = usePostHog();

  // Use prop unitId or URL param
  const unitId = propUnitId || paramUnitId;
  const onBack = propOnBack || (() => navigate(-1));

  const [images, setImages] = useState<UnitImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Fetch images for this unit
  // `silent` (a link refresh): keep the grid on screen and say nothing if it fails.
  const fetchImages = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!unitId) return;

    if (!silent) setIsLoading(true);
    try {
      const response = await authenticatedFetch(`/api/v1/units/${unitId}/images`);
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch images:', errorText);
        if (!silent) toast.error('Failed to load images. Please try again.');
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
      if (!silent) toast.error('Failed to load images. Please check your connection and try again.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    void fetchImages();
  }, [fetchImages]);

  // Photo links are signed for an hour (ISS-256). When one stops loading, fetch fresh links,
  // at most once every 5 minutes so a photo that is really gone can't loop.
  const lastLinkRefresh = useRef(0);
  const refreshLinks = useCallback(() => {
    const now = Date.now();
    if (now - lastLinkRefresh.current < 5 * 60 * 1000) return;
    lastLinkRefresh.current = now;
    void fetchImages({ silent: true });
  }, [fetchImages]);

  // Drag and drop upload
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!unitId || acceptedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress({ current: 0, total: acceptedFiles.length });

    try {
      let successCount = 0;
      let failCount = 0;

      // Upload files sequentially to avoid overwhelming the server
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        setUploadProgress({ current: i + 1, total: acceptedFiles.length });

        const formData = new FormData();
        formData.append('image', file);

        try {
          const response = await authenticatedFetch(`/api/v1/units/${unitId}/images`, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Failed to upload image:', errorText);
            failCount++;
          } else {
            successCount++;
          }
        } catch (error) {
          console.error('Upload error for file:', file.name, error);
          failCount++;
        }
      }

      // Show appropriate success/error messages
      if (failCount === 0 && successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}`);
        // Track successful upload
        if (posthog && unitId) {
          posthog.capture('gallery_images_uploaded', {
            unit_id: unitId,
            image_count: successCount,
          });
        }
      } else if (failCount > 0 && successCount > 0) {
        toast.warning(`Uploaded ${successCount} image${successCount > 1 ? 's' : ''}, but ${failCount} failed`);
        // Track partial success
        if (posthog && unitId) {
          posthog.capture('gallery_images_uploaded', {
            unit_id: unitId,
            image_count: successCount,
          });
        }
      } else if (failCount > 0) {
        toast.error(`Failed to upload ${failCount} image${failCount > 1 ? 's' : ''}. Please try again.`);
      }

      // Refresh images after upload
      await fetchImages();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('An unexpected error occurred during upload. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  }, [unitId, fetchImages]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    multiple: true,
    disabled: isUploading,
  });

  // Drag and drop reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);

      const reorderedImages = arrayMove(images, oldIndex, newIndex);

      // Update local state immediately for responsive UI
      setImages(reorderedImages);

      // Send reorder request to backend
      const imageOrders = reorderedImages.map((img, index) => ({
        id: img.id,
        display_order: index,
      }));

      try {
        const response = await authenticatedFetch(`/api/v1/units/${unitId}/images/reorder`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ imageOrders }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to reorder images:', errorText);
          toast.error('Failed to reorder images. Changes have been reverted.');
          // Revert on error
          await fetchImages();
        } else {
          toast.success('Images reordered successfully');
          // Track successful reorder
          if (posthog && unitId) {
            posthog.capture('gallery_images_reordered', {
              unit_id: unitId,
            });
          }
        }
      } catch (error) {
        console.error('Reorder error:', error);
        toast.error('Failed to reorder images. Please check your connection and try again.');
        // Revert on error
        await fetchImages();
      }
    }
  };

  // Delete image
  const handleDelete = async (imageId: string) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await authenticatedFetch(`/api/v1/units/${unitId}/images/${imageId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Image deleted successfully');
        // Track successful delete
        if (posthog && unitId) {
          posthog.capture('gallery_image_deleted', {
            unit_id: unitId,
            image_id: imageId,
          });
        }
        await fetchImages();
      } else {
        const errorText = await response.text();
        console.error('Failed to delete image:', errorText);
        toast.error('Failed to delete image. Please try again.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('An unexpected error occurred. Please check your connection and try again.');
    }
  };

  // Set primary image
  const handleSetPrimary = async (imageId: string) => {
    try {
      const response = await authenticatedFetch(`/api/v1/units/${unitId}/images/${imageId}/primary`, {
        method: 'PUT',
      });

      if (response.ok) {
        toast.success('Primary image updated successfully');
        // Track successful primary image set
        if (posthog && unitId) {
          posthog.capture('gallery_primary_image_set', {
            unit_id: unitId,
            image_id: imageId,
          });
        }
        await fetchImages();
      } else {
        const errorText = await response.text();
        console.error('Failed to set primary image:', errorText);
        toast.error('Failed to set primary image. Please try again.');
      }
    } catch (error) {
      console.error('Set primary error:', error);
      toast.error('An unexpected error occurred. Please check your connection and try again.');
    }
  };

  // Open lightbox at specific image
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    // Track lightbox view
    if (posthog && unitId && images[index]) {
      posthog.capture('gallery_image_viewed', {
        unit_id: unitId,
        image_id: images[index].id,
      });
    }
  };

  // Prepare lightbox slides
  const lightboxSlides = images.map((img) => ({
    src: img.large_webp_url ?? img.medium_webp_url ?? img.original_url ?? '',
    alt: img.caption || `Image ${img.display_order + 1}`,
  }));

  if (!unitId) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <p className="text-muted-foreground">No unit ID provided</p>
      </div>
    );
  }

  return (
    <>
      <motion.div
        className="h-full overflow-y-auto bg-background"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={onBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold">Image Gallery</h1>
                  <Badge variant="outline">{images.length} Images</Badge>
                </div>
                <p className="text-sm text-muted-foreground">Unit {unitId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="container max-w-6xl mx-auto px-4 py-6 space-y-6">
          {/* Upload Zone */}
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50 hover:bg-muted/50',
              isUploading && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            {isUploading ? (
              <p className="text-sm text-muted-foreground">
                {uploadProgress
                  ? `Uploading ${uploadProgress.current} of ${uploadProgress.total} images`
                  : 'Uploading...'}
              </p>
            ) : isDragActive ? (
              <p className="text-sm font-medium">Drop images here</p>
            ) : (
              <>
                <p className="text-sm font-medium mb-1">
                  Drag and drop images here, or click to browse
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports JPEG, PNG, and WebP (max 10MB per file)
                </p>
              </>
            )}
          </div>

          {/* Image Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading images...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ImageIcon className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <p className="text-sm text-muted-foreground">
                No images yet. Upload your first image to get started.
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={images.map((img) => img.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {images.map((image, index) => (
                    <SortableImageCard
                      key={image.id}
                      image={image}
                      index={index}
                      onDelete={handleDelete}
                      onSetPrimary={handleSetPrimary}
                      onOpenLightbox={openLightbox}
                      onLinkFailed={refreshLinks}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </motion.div>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        }}
      />
    </>
  );
}

// Sortable Image Card Component
interface SortableImageCardProps {
  image: UnitImage;
  index: number;
  onDelete: (imageId: string) => void;
  onSetPrimary: (imageId: string) => void;
  onOpenLightbox: (index: number) => void;
  /** A link stopped loading (they last an hour): fetch fresh ones. */
  onLinkFailed: () => void;
}

function SortableImageCard({
  image,
  index,
  onDelete,
  onSetPrimary,
  onOpenLightbox,
  onLinkFailed,
}: SortableImageCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  // Progressive loading state
  const [isLoaded, setIsLoaded] = useState(false);
  // The link that failed to load: show the placeholder for it. A fresh link is a new string,
  // so it gets its own try.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const unavailable = !image.thumb_webp_url || image.thumb_webp_url === failedUrl;
  // Without IntersectionObserver, load at once.
  const [isInView, setIsInView] = useState(() => typeof IntersectionObserver === 'undefined');
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    // Fallback: no IntersectionObserver means isInView started true
    if (typeof IntersectionObserver === 'undefined') return;

    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative aspect-square rounded-lg overflow-hidden bg-muted border border-border',
        isDragging && 'opacity-50 z-50'
      )}
    >
      {/* Progressive Image with Blur-Up Placeholder */}
      <div ref={containerRef} className="relative w-full h-full">
        {/* Blur placeholder - shown until image loads */}
        {!isLoaded && !unavailable && (
          <div
            className="absolute inset-0 bg-muted animate-pulse"
            aria-hidden="true"
          />
        )}

        {/* No link (it couldn't be signed) or it stopped loading: say so instead of a broken image (ISS-256) */}
        {unavailable && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <ImageIcon className="h-8 w-8 opacity-50" />
            <span className="text-xs">Photo unavailable</span>
          </div>
        )}

        {/* Actual image - only load when in viewport */}
        {isInView && !unavailable && image.thumb_webp_url && (
          <img
            ref={imgRef}
            src={image.thumb_webp_url}
            alt={image.caption || `Image ${index + 1}`}
            className={cn(
              'w-full h-full object-cover cursor-pointer transition-opacity duration-300',
              isLoaded ? 'opacity-100' : 'opacity-0'
            )}
            loading="lazy"
            decoding="async"
            onLoad={() => setIsLoaded(true)}
            onError={() => {
              setFailedUrl(image.thumb_webp_url ?? null);
              onLinkFailed();
            }}
            onClick={() => onOpenLightbox(index)}
            // Modern responsive images with srcset
            srcSet={image.medium_webp_url ? `${image.thumb_webp_url} 150w, ${image.medium_webp_url} 600w` : undefined}
            sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw"
          />
        )}
      </div>

      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 p-1.5 bg-background/80 backdrop-blur rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <GripVertical className="h-4 w-4" />
      </div>

      {/* Primary Badge */}
      {image.is_primary && (
        <div className="absolute top-2 right-2">
          <Badge variant="default" className="text-xs gap-1">
            <Star className="h-3 w-3 fill-current" />
            Primary
          </Badge>
        </div>
      )}

      {/* Hover Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex gap-1">
          {!image.is_primary && (
            <Button
              variant="secondary"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => onSetPrimary(image.id)}
            >
              <Star className="h-3 w-3 mr-1" />
              Set Primary
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            className="h-7 px-2"
            onClick={() => onDelete(image.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
